import { Link } from "react-router";
import type { ReactNode, ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?:
    "default" | "primary" | "hero" | "secondary" | "outline" | "destructive";
  size?: "sm" | "md" | "lg";
  href?: string;
  external?: boolean;
  icon?: ReactNode;
  isDark?: boolean;
}

export default function Button({
  children,
  variant = "default",
  size = "md",
  href,
  external = false,
  icon,
  isDark,
  className = "",
  ...props
}: ButtonProps) {
  const baseClasses = cn(
    "relative inline-flex items-center justify-center",
    "font-body text-sm font-medium uppercase tracking-[0.1em]",
    "no-underline cursor-pointer overflow-hidden",
    "transition-[color] duration-400 ease-[cubic-bezier(0.3,0,0.3,1)]",
    'before:content-[""] before:absolute before:inset-0 before:z-[-1]',
    "before:transition-transform before:duration-400 before:ease-[cubic-bezier(0.3,0,0.3,1)]",
  );

  const variantClasses = {
    default: cn(
      "border border-border bg-transparent text-heading",
      "before:bg-accent before:scale-x-0 before:origin-right",
      "hover:text-white hover:border-accent",
      "hover:before:scale-x-100 hover:before:origin-left",
    ),
    primary: cn(
      "border border-accent bg-accent text-white",
      "before:bg-white before:scale-x-0 before:origin-right",
      "hover:text-accent",
      "hover:before:scale-x-100 hover:before:origin-left",
    ),
    hero: "",
    secondary: cn(
      "border border-border bg-transparent text-heading rounded-lg",
      "hover:bg-white/5",
    ),
    outline: cn(
      "border border-border bg-transparent text-heading rounded-lg",
      "hover:bg-white/5 hover:border-heading",
    ),
    destructive: cn(
      "border border-red-500 bg-red-500 text-white rounded-lg",
      "hover:bg-red-600 hover:border-red-600",
    ),
  };

  const sizeClasses = {
    sm: "py-3 px-6 text-xs",
    md: "py-4 px-8",
    lg: "py-5 px-10 text-base",
  };

  const iconClasses = icon
    ? "gap-2 [&_svg]:w-[1em] [&_svg]:h-[1em] [&_svg]:transition-transform [&_svg]:duration-300 hover:[&_svg]:translate-x-1"
    : "";

  const heroClasses = cn(
    "relative inline-block isolate",
    "font-body text-base font-medium uppercase tracking-[0.1em]",
    "no-underline cursor-pointer overflow-hidden",
    "border-none bg-transparent",
    isDark ? "text-zinc-950" : "text-heading",
    "leading-none p-2.5",
    "transition-[color] duration-400 ease-[cubic-bezier(0.3,0,0.3,1)]",
    'before:content-[""] before:absolute before:top-0 before:left-0',
    "before:w-6 before:h-full before:bg-accent before:z-[-1]",
    "before:skew-x-0",
    "before:transition-all before:duration-400 before:ease-[cubic-bezier(0.3,0,0.3,1)]",
    "hover:text-white",
    "hover:before:left-[-10%] hover:before:w-[120%] hover:before:h-[110%]",
    "hover:before:skew-x-[-10deg]",
  );

  const classes =
    variant === "hero"
      ? cn(heroClasses, className)
      : cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          iconClasses,
          className,
        );

  const content = (
    <>
      {children}
      {icon}
    </>
  );

  if (href && (external || href.startsWith("#"))) {
    return (
      <a
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
        className={classes}
      >
        {content}
      </a>
    );
  }

  if (href) {
    return (
      <Link to={href} viewTransition className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <button className={classes} {...props}>
      {content}
    </button>
  );
}
